import React from 'react';
import { Link } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Icon from '../../components/AppIcon';

const RankingSystemPage = () => {
  const reportRules = [
    {
      title: 'Same interview setup',
      body: 'Report rank compares only attempts with the same interview type, planned duration, difficulty, and company.',
      icon: 'SlidersHorizontal'
    },
    {
      title: 'Score first',
      body: 'Higher overall interview score always ranks above a lower score inside that cohort.',
      icon: 'Trophy'
    },
    {
      title: 'Time breaks score ties',
      body: 'If two candidates have the same score, the candidate who completed the interview in less actual time ranks higher.',
      icon: 'Timer'
    }
  ];

  const globalRules = [
    {
      title: 'Normalize every report',
      body: 'Each report is converted into a percentile inside its own exact interview configuration before it affects global standing.',
      icon: 'Gauge'
    },
    {
      title: 'Average normalized performance',
      body: 'Your global performance score is the average of your normalized report percentiles across completed interviews.',
      icon: 'ChartNoAxesCombined'
    },
    {
      title: 'Rank across the whole system',
      body: 'Final rank compares that normalized performance score against all public non-admin users on prepxai.',
      icon: 'Globe2'
    }
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <NavigationHeader />

      <main className="mx-auto w-full max-w-5xl px-6 pt-24 pb-16">
        <div className="mb-10 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">
              <Icon name="Info" size={13} />
              Ranking Method
            </div>
            <h1 className="text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">How ranks and percentiles work</h1>
            <p className="mt-3 text-sm font-medium leading-6 text-slate-500">
              prepxai uses two ranking scopes: your report rank is based on the exact interview configuration you attempted, while your final global rank is based on normalized performance across the whole platform.
            </p>
          </div>

          {/* <Link
            to="/leaderboard"
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 text-xs font-black text-slate-700 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
          >
            Leaderboard
            <Icon name="ArrowRight" size={15} />
          </Link> */}
        </div>

        <section className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-950 text-white">
              <Icon name="FileText" size={17} />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-950">Report Rank</h2>
              <p className="text-xs font-bold text-slate-400">Shown on individual interview reports</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {reportRules.map((rule) => (
              <div key={rule.title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
                  <Icon name={rule.icon} size={17} />
                </div>
                <h3 className="text-sm font-black text-slate-900">{rule.title}</h3>
                <p className="mt-2 text-xs font-medium leading-5 text-slate-500">{rule.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-5">
            <p className="text-xs font-bold leading-6 text-slate-600">
              Example: A 45 minute, medium, coding interview for Google is ranked only against other 45 minute, medium, coding interviews for Google. A candidate with 86 points in 28 minutes ranks above another candidate with 86 points in 34 minutes.
            </p>
          </div>
        </section>

        <section>
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500 text-slate-950">
              <Icon name="Medal" size={17} />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-950">Final Global Rank</h2>
              <p className="text-xs font-bold text-slate-400">Shown on dashboard metrics and leaderboard</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {globalRules.map((rule) => (
              <div key={rule.title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                  <Icon name={rule.icon} size={17} />
                </div>
                <h3 className="text-sm font-black text-slate-900">{rule.title}</h3>
                <p className="mt-2 text-xs font-medium leading-5 text-slate-500">{rule.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-950 p-5 text-white">
              <h3 className="text-sm font-black">Why we normalize</h3>
              <p className="mt-2 text-xs font-medium leading-5 text-slate-300">
                Raw scores from different interview setups are not equally hard to earn. Normalization lets a strong hard interview performance beat an easier interview with a slightly higher raw score.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-black text-slate-900">Global ties</h3>
              <p className="mt-2 text-xs font-medium leading-5 text-slate-500">
                Users with the same normalized performance score share the same global rank. Report-level ties are separated by actual completion time when scores match.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default RankingSystemPage;
